import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchZoneLogoById } from "services/zone";
import zoneLocalStorageId from "utils/zone";
import {
  DEFAULT_APP_LOGO_SRC,
  DEFAULT_LOGO_SRC,
  getBase64ImageSrc,
} from "utils/logo";

const getCurrentZoneId = () => {
  const zoneId = zoneLocalStorageId();
  return Number.isNaN(zoneId) || !zoneId ? null : zoneId;
};

const getLogoData = (response) => {
  const body = response?.data;
  const value =
    body?.file_data ||
    body?.logo_data ||
    body?.zone_logo ||
    body?.logo ||
    body?.data ||
    body;

  return typeof value === "string" ? value : "";
};

const DynamicZoneLogo = ({
  alt = "Zone Logo",
  fallbackSrc = DEFAULT_LOGO_SRC,
  className,
  style,
  width,
  height,
  title = "Logo",
  ...props
}) => {
  const [zoneId, setZoneId] = useState(getCurrentZoneId);
  const queryClient = useQueryClient();

  useEffect(() => {
    const syncZoneId = () => setZoneId(getCurrentZoneId());

    window.addEventListener("storage", syncZoneId);
    window.addEventListener("zoneChanged", syncZoneId);

    return () => {
      window.removeEventListener("storage", syncZoneId);
      window.removeEventListener("zoneChanged", syncZoneId);
    };
  }, []);

  useEffect(() => {
    const handleZoneLogoChanged = (event) => {
      const changedZoneId = Number(event?.detail);
      if (!changedZoneId || changedZoneId === zoneId) {
        queryClient.invalidateQueries(["fetch-zone-logo-by-id", zoneId]);
      }
    };

    window.addEventListener("zoneLogoChanged", handleZoneLogoChanged);
    return () =>
      window.removeEventListener("zoneLogoChanged", handleZoneLogoChanged);
  }, [queryClient, zoneId]);

  const { data: zoneLogoData } = useQuery(
    ["fetch-zone-logo-by-id", zoneId],
    () => fetchZoneLogoById(zoneId),
    {
      enabled: !!zoneId,
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: false,
      select: getLogoData,
    },
  );

  const logoSrc = getBase64ImageSrc(zoneLogoData) || fallbackSrc;

  return (
    <img
      src={logoSrc}
      alt={alt}
      title={title}
      className={className}
      width={width}
      height={height}
      style={style}
      onError={(event) => {
        if (event.currentTarget.src !== fallbackSrc) {
          event.currentTarget.src = fallbackSrc;
        }
      }}
      {...props}
    />
  );
};

export { DEFAULT_APP_LOGO_SRC, DEFAULT_LOGO_SRC };
export default DynamicZoneLogo;
